import { CheckOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { EditableProTable } from '@ant-design/pro-components';
import type { ParamsType } from '@ant-design/pro-provider';
import type { EditableProTableProps } from '@ant-design/pro-table/lib/components/EditableTable';
import { Button, ButtonProps, Empty, Skeleton, Tooltip } from 'antd';
import type { Key } from 'antd/lib/table/interface';
import classnames from 'classnames';
import { useMemo, useState } from 'react';
import { GTableProvider, useGTableContext } from './contexts';
import styles from './GTable.module.less';
import { ActionsRenderOptions, GTableProps } from './typings';

const defaultBtnProps: ButtonProps = {
  size: 'small',
  ghost: false,
  type: 'text',
  className: 'value-actions-btn',
};

const GTable = <
  DataType extends Record<string, any>,
  Params extends ParamsType = ParamsType,
  ValueType = 'text',
>({
  addButtonProps,
  addBtnText = 'Add',
  className,
  editable,
  enableRecordCreator,
  enableRowSelection,
  actionsRenderOptions,
  onAddRecordClick,
  options,
  pagination = false,
  recordCreatorProps,
  rowKey = 'id',
  rowSelection,
  size = 'small',
  search = false,
  emptyContainer,
  loading,
  customDeleteButton,
  ...props
}: EditableProTableProps<DataType, Params, ValueType> & GTableProps) => {
  const { actionRef, selectedRowKeys, onRowSelectionChange } = useGTableContext();
  const [editableKeys, setEditableKeys] = useState<Array<Key>>();

  const actionsRenderOptionsDefault: ActionsRenderOptions = {
    save: true,
    cancel: true,
  };

  const mergedActionsRenderOptions: ActionsRenderOptions = {
    ...actionsRenderOptionsDefault,
    ...actionsRenderOptions,
  };

  const saveChangesFirst = 'You must save your changes first';
  const initLocale = useMemo(() => {
    const emptyComp = !emptyContainer ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className={styles.sktcontainer} />
    ) : (
      emptyContainer
    );

    return {
      emptyText: loading ? (
        <Skeleton
          active
          className={styles.sktcontainermain}
          title={false}
          paragraph={{ rows: 4, width: '100%' }}
        />
      ) : (
        emptyComp
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  return (
    <>
      <EditableProTable<DataType, Params, ValueType>
        {...props}
        actionRef={actionRef}
        className={classnames(styles.table, className)}
        editable={{
          type: 'single',
          deletePopconfirmMessage: 'Are you sure ?',
          onlyAddOneLineAlertMessage: saveChangesFirst,
          onlyOneLineEditorAlertMessage: saveChangesFirst,
          actionRender: (row, config, defaultDoms) => [
            mergedActionsRenderOptions?.save && (
              <Tooltip placement="bottom" title="Save">
                <Button {...defaultBtnProps}>{defaultDoms.save}</Button>
              </Tooltip>
            ),
            mergedActionsRenderOptions?.cancel && (
              <Tooltip placement="bottom" title="Cancel">
                <Button {...defaultBtnProps}>{defaultDoms.cancel}</Button>
              </Tooltip>
            ),
            mergedActionsRenderOptions?.delete && (
              <Tooltip placement="bottom" title="Delete">
                <Button {...defaultBtnProps}>
                  {customDeleteButton ? (
                    <DeleteOutlined
                      className={styles.deletebutton}
                      onClick={() => customDeleteButton(row)}
                    />
                  ) : (
                    defaultDoms.delete
                  )}
                </Button>
              </Tooltip>
            ),
          ],
          cancelText: <CloseOutlined />,
          saveText: <CheckOutlined />,
          deleteText: <DeleteOutlined />,
          editableKeys,
          onChange: setEditableKeys,
          ...editable,
        }}
        ghost
        options={{
          density: false,
          reload: true,
          fullScreen: false,
          setting: true,
          ...options,
        }}
        pagination={pagination}
        rowKey={rowKey}
        rowSelection={
          enableRowSelection && {
            selectedRowKeys,
            onChange: onRowSelectionChange,
            ...rowSelection,
          }
        }
        recordCreatorProps={recordCreatorProps || false}
        search={search}
        size={size}
        loading={false}
        locale={initLocale}
      />
      {enableRecordCreator && (
        <div className={styles.toolbar}>
          <Button
            type="primary"
            size="middle"
            shape="round"
            {...addButtonProps}
            onClick={onAddRecordClick}
          >
            {addBtnText}
          </Button>
        </div>
      )}
    </>
  );
};

const GTableContainer = <
  DataType extends Record<string, any>,
  Params extends ParamsType = ParamsType,
  ValueType = 'text',
>({
  actionRef,
  ...props
}: EditableProTableProps<DataType, Params, ValueType> & GTableProps) => (
  <GTableProvider actionRef={actionRef}>
    <GTable<DataType, Params, ValueType> {...props} />
  </GTableProvider>
);

export default GTableContainer;
